/**
 * Dev-only mock API — lets the full microsite run with NO .env and NO network.
 *
 * Activation (BOTH required, so plain `npm start` is unaffected):
 *   1. `process.env.NODE_ENV === 'development'` (the require() in axios-instance.js
 *      is inside that guard, so none of this ships in a production build), and
 *   2. the page URL uses a mock short-link slug: `?sl=mock-<type>`.
 *
 * It hooks the axios instance's adapter, so the app's real code paths all run —
 * shortlink resolution, device registration, content catch, impression tracking,
 * terms fetch — they just get fabricated responses (see ./mockData.js).
 *
 * USAGE — open one of (pretty path or ?sl= form; legacy mock- alias also works):
 *   http://localhost:3000/sample-offer        offer w/ scratcher, barcode + timer
 *   http://localhost:3000/sample-prize        prize variant (Redeem Prize flow)
 *   http://localhost:3000/sample-url          url content w/ link buttons
 *   http://localhost:3000/sample-message      message w/ in-body image
 *   http://localhost:3000/sample-wallet       wallet view (clear-wallet flow)
 *   (equivalent: /?sl=sample-offer etc.)
 *
 * Optional toggles (append to the URL):
 *   &mock_scratcher=0|1   scratch overlay (default: on for offer/prize)
 *   &mock_overlay=block|fallback  overlay artwork: default is the bundled
 *                         McDonald's "SCRATCH TO REVEAL" art; 'block' = SVG
 *                         color block; 'fallback' = no url (exercises the
 *                         Content.js swipe-to-reveal fallback)
 *   &mock_timer=0         disable the redemption countdown timer
 *   &mock_agegate=1       enable the 21+ age gate
 *   &mock_terms=0         remove terms & conditions
 *   &mock_barcode=QRCode  barcode format: QRCode|Code128|Code39|EAN-8|EAN-13|
 *                         UPC-E|UPC-A|ITF14|PDF417|Aztec|DataMatrix
 *   &mock_method=1        redemption: 2=in-store barcode (default), 1=online,
 *                         download=app-download flow
 *   &mock_passes=1        apple/google wallet passes (Google needs the gapi script)
 *   &mock_ct=1            add click-through buttons 1 & 2
 *   &mock_qr=1            add the "open QR scanner" button
 *   &mock_tags=card      content tags (comma-separated). 'card' switches on the
 *                        floating-card presentation; also: auto-save,
 *                        no-action-buttons
 *   &mock_expired=1       content already expired
 *   &mock_redirect=1      url type: auto-redirect (a8_redirect=1) → shows the
 *                         "you're being redirected" interstitial, then navigates.
 *                         =stay keeps the interstitial on screen (same-page hash
 *                         target) so it can be inspected.
 */
import {
  buildMockContent,
  mockDevice,
  mockTermsBody,
  mockWalletItems,
  mockAndroidPass,
} from './mockData';

const MOCK_TYPES = ['offer', 'prize', 'url', 'message', 'wallet'];
const LATENCY_MS = 150; // small fake latency so loaders behave naturally

const parseToggles = (params) => {
  const opts = {};
  const flag = (key) => (params.has(key) ? params.get(key) !== '0' : undefined);

  const scratcher = flag('mock_scratcher');
  if (scratcher !== undefined) opts.scratcher = scratcher;
  if (params.has('mock_overlay')) opts.overlay = params.get('mock_overlay');
  const timer = flag('mock_timer');
  if (timer !== undefined) opts.timer = timer;
  if (flag('mock_agegate')) opts.ageGate = true;
  const terms = flag('mock_terms');
  if (terms !== undefined) opts.terms = terms;
  if (params.has('mock_barcode')) opts.barcode = params.get('mock_barcode');
  if (params.has('mock_method')) {
    const m = params.get('mock_method');
    opts.method = m === 'download' ? 'download' : Number(m);
  }
  if (flag('mock_passes')) opts.passes = true;
  if (flag('mock_ct')) opts.clickThrough = true;
  if (flag('mock_qr')) opts.qrScanner = true;
  if (params.has('mock_tags')) {
    opts.tags = params.get('mock_tags').split(',').map((t) => t.trim()).filter(Boolean);
  }
  if (flag('mock_expired')) opts.expired = true;
  // url type: exercise the auto-redirect (a8_redirect=1) interstitial.
  // 'stay' points the redirect at a same-page hash so the notice stays
  // on screen for eyeballing; any other truthy value uses an external target.
  if (params.has('mock_redirect') && params.get('mock_redirect') !== '0') {
    opts.redirect = params.get('mock_redirect') === 'stay' ? 'stay' : true;
  }

  return opts;
};

export const installMockApiIfRequested = (instance) => {
  const params = new URLSearchParams(window.location.search);

  // Pretty sample routes: /sample-offer, /sample-prize, … behave exactly like
  // ?sl=sample-<type>. Inject the slug into the real URL (keeping the pretty
  // path in the address bar) so App.js's own param parsing picks it up.
  const pathMatch = window.location.pathname.match(/^\/sample-([a-z]+)$/);
  if (pathMatch && !params.has('sl')) {
    params.set('sl', `sample-${pathMatch[1]}`);
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}?${params.toString()}`
    );
  }

  const slug = params.get('sl');
  // 'sample-' is the canonical prefix; 'mock-' kept as a legacy alias.
  if (!slug || !/^(sample|mock)/.test(slug)) return;

  const bare = slug.replace(/^(sample|mock)-?/, '');
  const mode = MOCK_TYPES.includes(bare) ? bare : 'offer';
  const opts = parseToggles(params);

  const realAdapter = instance.defaults.adapter;

  const respond = (config, data, status = 200) =>
    new Promise((resolve) =>
      setTimeout(
        () => resolve({ data, status, statusText: 'OK', headers: {}, config, request: {} }),
        LATENCY_MS
      )
    );

  instance.defaults.adapter = (config) => {
    const url = config.url || '';
    const method = (config.method || 'get').toLowerCase();
    // In-page call ledger — lets QA tooling assert exact call counts without
    // relying on console capture (which can duplicate entries).
    (window.__a8MockCalls = window.__a8MockCalls || []).push(`${method.toUpperCase()} ${url}`);
    const log = (what) =>
      console.info(`%c[mockApi] ${method.toUpperCase()} ${url} → ${what}`, 'color:#E8A030');

    // Short-link resolution — points the app at mock trigger params.
    if (method === 'get' && url.includes('/shortlink')) {
      log(`shortlink (mode: ${mode})`);
      const resolved =
        mode === 'wallet'
          ? `${window.location.origin}/?mock_wallet=1&fp_enabled=false&geolocation=false`
          : `${window.location.origin}/?triggerable_type=trigger&triggerable_id=22&client_id=1&fp_enabled=false&geolocation=false`;
      return respond(config, { url: resolved });
    }

    // Device registration.
    if (method === 'post' && url.startsWith('device/')) {
      log('device registered');
      if (mode === 'wallet') {
        // The wallet view only shows when the page URL has no `sl`/trigger params
        // (App.checkParams reads window.location.search after registration), so
        // drop the mock slug from the address bar before responding.
        const cleaned = new URLSearchParams(window.location.search);
        cleaned.delete('sl');
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}?${cleaned.toString()}`
        );
      }
      return respond(config, mockDevice(config.headers?.['x-device-uuid']));
    }

    // Content catch (by trigger type or trigger uuid) — the main payload.
    if (method === 'post' && (url.includes('/catch?') || url.includes('content/deliver'))) {
      log(`content delivered (type: ${mode})`);
      return respond(config, [buildMockContent(mode, opts)]);
    }

    // Time-delayed delivery targets.
    if (method === 'get' && url.includes('trigger/timestamps')) {
      log('timestamps (none)');
      return respond(config, []);
    }

    // Terms & conditions body.
    if (method === 'get' && url.includes('content-terms/')) {
      log('terms body');
      return respond(config, mockTermsBody());
    }

    // Wallet passes (before the generic /content/ matchers below).
    if (method === 'post' && url.includes('androidpass')) {
      log('android pass jwt');
      return respond(config, mockAndroidPass());
    }
    if (method === 'post' && url.includes('applepass')) {
      log('apple pass (stub)');
      return respond(config, { ok: true });
    }

    // Impression tracking: PUT /device/{uuid}/content/{id}?status=N
    if (method === 'put' && url.includes('/content/')) {
      const status = new URLSearchParams(url.split('?')[1] || '').get('status');
      log(`impression status=${status}`);
      return respond(config, { message: `(mock) impression recorded: status=${status}` });
    }

    // Event logging.
    if (method === 'post' && url.includes('/event')) {
      log('event logged');
      return respond(config, { message: '(mock) event logged' });
    }

    // Wallet content list / clear.
    if (method === 'get' && url.includes('/content?status')) {
      log('wallet items');
      return respond(config, mockWalletItems());
    }
    if (method === 'delete' && url.includes('/content')) {
      log('wallet cleared');
      return respond(config, { message: '(mock) Wallet cleared successfully' });
    }

    // Anything unrecognized falls through to the real adapter (real network).
    console.warn(`[mockApi] unhandled ${method.toUpperCase()} ${url} — passing through`);
    return realAdapter(config);
  };

  console.info(
    `%c[mockApi] ACTIVE — mode: ${mode} — all API calls are mocked (dev only). ` +
      `Toggles: ${JSON.stringify(opts)}`,
    'color:#fff;background:#E8A030;padding:2px 6px;border-radius:3px;font-weight:bold'
  );
};
